import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import { StabilityRuleSource } from '../../../database/schema.js';
import { zql } from '../../../schema.js';
import { pickActorFromUserActions, pickTimestampFromUserActions } from '../../../util/index.js';
import { itemsDetail, valueItem } from '../../../util/event-log-items.js';
import { EventLogResources, addEventLog } from '../../util.js';
import { findDrugName, findProfileNames } from '../../utils/log-names.js';
import { deleteStabilityRule } from '../../utils/stability-rules.js';
export async function deleteMedicationProfileReconstitutionDiluent(tx, args, userActions) {
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const deletedMedicationProfileDiluent = {
        id: args.medicationProfileReconstitutionDiluentId,
        deletedAt: timestamp,
        deletedBy: actor,
    };
    const promises = [tx.mutate.medicationProfileAllowedReconstitutionDiluents.update(deletedMedicationProfileDiluent)];
    if (tx.location === 'server') {
        const patches = createPatch({}, deletedMedicationProfileDiluent);
        const relation = await tx.run(zql.medicationProfileAllowedReconstitutionDiluents.where('id', deletedMedicationProfileDiluent.id).one());
        const { itemName } = await findProfileNames(tx, relation?.medicationProfileId);
        const diluent = await findDrugName(tx, relation?.solutionDrugId);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.medicationProfileAllowedReconstitutionDiluent,
            resourceId: deletedMedicationProfileDiluent.id,
            action: 'delete',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            status: 'WARNING',
            details: { itemName, ...itemsDetail(valueItem('diluent', diluent)) },
        }));
    }
    await Promise.all(promises);
    return deletedMedicationProfileDiluent;
}
export async function deleteMedicationProfileStabilityRule(tx, { medicationStabilityRule }, userActions) {
    const stabilityRule = await tx.run(zql.stabilityRules.where('id', medicationStabilityRule.stabilityRuleId).where('deletedAt', 'IS', null).one());
    if (stabilityRule?.source === StabilityRuleSource.enum.MEDICATION_PROFILE) {
        const itemName = tx.location === 'server'
            ? (await findProfileNames(tx, medicationStabilityRule.medicationProfileId)).itemName
            : undefined;
        await deleteStabilityRule(tx, medicationStabilityRule.stabilityRuleId, userActions, itemName);
    }
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    const deletedMedicationProfileStabilityRule = {
        id: medicationStabilityRule.id,
        deletedAt: timestamp,
        deletedBy: actor,
    };
    const promises = [tx.mutate.medicationStabilityRules.update(deletedMedicationProfileStabilityRule)];
    if (tx.location === 'server') {
        const patches = createPatch({}, deletedMedicationProfileStabilityRule);
        const { itemName, profileName } = await findProfileNames(tx, medicationStabilityRule.medicationProfileId);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.medicationProfileStabilityRule,
            resourceId: deletedMedicationProfileStabilityRule.id,
            action: 'delete',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            status: 'WARNING',
            details: { itemName, ...itemsDetail(valueItem('profile', profileName)) },
        }));
    }
    await Promise.all(promises);
    return deletedMedicationProfileStabilityRule;
}
export async function deleteMedicationProfile(tx, { medicationProfile }, userActions) {
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    const deletedMedicationProfile = {
        id: medicationProfile.id,
        deletedAt: timestamp,
        deletedBy: actor,
    };
    const promises = [tx.mutate.medicationProfiles.update(deletedMedicationProfile)];
    if (tx.location === 'server') {
        const patches = createPatch({}, deletedMedicationProfile);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.medicationProfile,
            resourceId: deletedMedicationProfile.id,
            action: 'delete',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            status: 'WARNING',
            details: {
                id: medicationProfile.id,
                name: medicationProfile.name,
                itemName: await findDrugName(tx, medicationProfile.drugCatalogItemId),
            },
        }));
    }
    await Promise.all(promises);
    if (medicationProfile.stabilityRules) {
        await Promise.all(medicationProfile.stabilityRules.map(stabilityRule => deleteMedicationProfileStabilityRule(tx, { medicationStabilityRule: stabilityRule }, userActions)));
    }
    if (medicationProfile.allowedReconstitutionDiluents) {
        await Promise.all(medicationProfile.allowedReconstitutionDiluents.map(medicationProfileDiluent => deleteMedicationProfileReconstitutionDiluent(tx, { medicationProfileReconstitutionDiluentId: medicationProfileDiluent.id }, userActions)));
    }
    return deletedMedicationProfile;
}
export async function deleteMedicationProfiles(tx, { medicationProfiles }, userActions) {
    await Promise.all(medicationProfiles.map(profile => deleteMedicationProfile(tx, { medicationProfile: profile }, userActions)));
}
//# sourceMappingURL=delete.js.map